<script setup>
import Button from '@/Components/Button/Button.vue';
import { mapErrors } from '@/utilities';
import { useForm } from '@inertiajs/vue3';
import { inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
    vacancy: Object,
});

const submittedForm = ref(false);
const route = inject('route');

const { t } = useI18n();

const form = useForm({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    birthday: '',
    place: '',
    cv: null,
    motivation: null,
    accepted: false,
});

const sendForm = () => {
    form.transform((data) => {
        if (data.cv?.length === 1) {
            data.cv = data.cv[0].file;
        }

        if (data.motivation?.length === 1) {
            data.motivation = data.motivation[0].file;
        }

        data.vacancy_id = props.vacancy.id;

        return data;
    }).post(route('api.vacancy'), {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
            submittedForm.value = true;
        },
        onError: (errors) => {
            form.errors = mapErrors(errors, (error) => [error]);
        },
    });
};
</script>

<template>
    <div v-if="submittedForm">
        <p>
            {{ t('Thank you for your application!') }}
        </p>
    </div>

    <FormKit type="form" id="jop-application-form" @submit="sendForm" :actions="false" v-else>
        <div class="grid gap-6 md:grid-cols-2">
            <div>
                <FormKit
                    type="text"
                    name="first_name"
                    :label="t('First name *')"
                    v-model="form.first_name"
                    :errors="form.errors.first_name"
                    validation="required|first_name"
                    :value="form.first_name"
                    validate="required"
                />

                <FormKit
                    type="text"
                    name="last_name"
                    :label="t('Last name *')"
                    v-model="form.last_name"
                    :errors="form.errors.last_name"
                    validation="required|last_name"
                    :value="form.last_name"
                    validate="required"
                />

                <FormKit
                    type="email"
                    name="email"
                    :label="t('E-mail *')"
                    validation="required|email"
                    :errors="form.errors.email"
                    v-model="form.email"
                />

                <FormKit
                    type="text"
                    name="phone"
                    :label="t('Phone *')"
                    v-model="form.phone"
                    :errors="form.errors.phone"
                    :value="form.phone"
                    validation="required|phone"
                />

                <FormKit
                    type="text"
                    name="birthday"
                    :label="t('Birthday *')"
                    v-model="form.birthday"
                    :errors="form.errors.birthday"
                    :value="form.birthday"
                    validation="required|birthday"
                />

                <FormKit
                    type="text"
                    name="place"
                    :label="t('Place')"
                    v-model="form.place"
                    :errors="form.errors.place"
                    :value="form.place"
                />
            </div>
            <div>
                <div class="relative">
                    <FormKit
                        type="file"
                        :label="t('Upload C.V *')"
                        :help="t('Choose file')"
                        accept=".pdf,.doc,.docx"
                        validation="required"
                        outer-class="$reset"
                        v-model="form.cv"
                        :errors="form.errors.cv"
                        @input="event?.target?.files[0] ? (form.cv = event?.target?.files[0]) : null"
                    />

                    <FormKit
                        type="file"
                        :label="t('Upload Motivation *')"
                        :help="t('Choose file')"
                        accept=".pdf,.doc,.docx"
                        validation="required"
                        outer-class="$reset"
                        v-model="form.motivation"
                        :errors="form.errors.motivation"
                        @input="event?.target?.files[0] ? (form.motivation = event?.target?.files[0]) : null"
                    />

                    <Button
                        type="button"
                        @click="sendForm"
                        :label="t('Send application')"
                        :form="form"
                        class="w-auto mt-6"
                    />
                </div>
            </div>
        </div>
    </FormKit>
</template>
